import { cx, MarkerIcon } from "../shared/markers";
import ContactRow from "./ContactRow";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import { fromPrice } from "./offers";
import { sisterOf, sisterVars } from "./SisterCard";
import type { OfferCard, PartnerContact, PartnerMapContent } from "./types";
import styles from "./PartnerMap.module.scss";

/** One card of the offers tab: the photo with its kind and "from" price, the name and what it is, its prices or menu or rating, and the button to book it. */
const Card = ({ card, content, onContact }: { card: OfferCard; content: PartnerMapContent; onContact: (channel: string) => void }) => {
	const t = strings(content.lang);
	const from = fromPrice(card);
	return (
		<article className={styles.offer} id={`partnermap-offer-${card.id}`}>
			<div className={styles.offerPh}>
				<img src={card.photo} alt="" loading="lazy" style={card.pos ? { objectPosition: card.pos } : undefined} />
				<span className={styles.offerKind}>
					<MarkerIcon name={card.icon} size={15} />
					{card.kind}
				</span>
				{from && <span className={styles.offerFrom}>{t.fromPrice(from)}</span>}
			</div>
			<div className={styles.offerBd}>
				<h3>{card.title}</h3>
				<p>{card.note}</p>
				{card.prices && (
					<table className={styles.ptab}>
						<thead>
							<tr>
								<th />
								{card.prices.columns.map((column, i) => (
									<th key={i}>{column}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{card.prices.rows.map(([name, ...cells]) => (
								<tr key={name}>
									<td>{name}</td>
									{cells.map((cell, i) => (
										<td key={i}>{cell}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				)}
				{card.menu && (
					<ol className={styles.menu3}>
						{card.menu.map((dish) => (
							<li key={dish}>{dish}</li>
						))}
					</ol>
				)}
				{card.rating && (
					<div className={styles.stars}>
						<i>★★★★★</i>
						{card.rating.score}
						<span>· {t.reviewsOn(card.rating.reviews, card.rating.source)}</span>
					</div>
				)}
				{card.perks && (
					<div className={styles.perks}>
						{card.perks.map((perk) => (
							<span key={perk}>{perk}</span>
						))}
					</div>
				)}
				<div className={styles.acts}>
					<a
						id={`partnermap-offer-${card.id}-book`}
						className={cx(styles.btn, card.dark ? styles.night : styles.primary)}
						href={card.link}
						target="_blank"
						rel="noopener"
						onClick={() => onContact(`offer_${card.id}`)}
					>
						{card.cta}
					</a>
				</div>
			</div>
		</article>
	);
};

interface OffersTabProps {
	content: PartnerMapContent;
	/** where to book and reach the restaurant, for the row that ends the tab; none: the tab ends with its small print */
	contact?: PartnerContact;
	onOpenPlace: (slug: string) => void;
	onContact: (channel: string) => void;
}

/**
 * The offers tab of a restaurant or bar ("Eat & drink here"): what it serves and what it costs as cards, the sister restaurant's card, "Book & contact" and the
 * small print of the prices. The page's disclaimer is not here: it is about the map's walking times.
 */
const OffersTab = ({ content, contact, onOpenPlace, onContact }: OffersTabProps) => {
	const { offers } = content;
	const t = strings(content.lang);
	const found = sisterOf(content);
	if (!offers) return null;
	return (
		<>
			<p className={styles.intro}>{offers.intro}</p>
			{offers.cards.map((card) => (
				<Card key={card.id} card={card} content={content} onContact={onContact} />
			))}
			{found && (
				<article className={cx(styles.offer, styles.offerSister)} id="partnermap-offer-sister" style={sisterVars(found.sister)}>
					<div className={styles.offerPh}>
						<img src={found.place.photo} alt="" loading="lazy" />
						<span className={styles.offerKind}>{t.sister}</span>
					</div>
					<div className={styles.offerBd}>
						<img className={styles.offerLogo} src={found.sister.logoOnLight ?? found.sister.logo} alt={found.place.name} />
						<h3>{found.sister.offerTitle}</h3>
						<p>{found.place.blurb}</p>
						<div className={styles.acts}>
							<a className={cx(styles.btn, styles.sisterBtn)} href={found.sister.book} target="_blank" rel="noopener" onClick={() => onContact("sister_book")}>
								{t.bookAt(found.sister.shortName)}
							</a>
							<button type="button" className={styles.btn} onClick={() => onOpenPlace(found.place.slug)}>
								<UiIcon name="directions" />
								{t.onMap}
							</button>
						</div>
					</div>
				</article>
			)}
			{contact && (
				<>
					<div className={styles.sec}>{t.bookAndContact}</div>
					<ContactRow content={content} contact={contact} onContact={onContact} />
				</>
			)}
			<p className={styles.disc}>{offers.fineprint}</p>
		</>
	);
};

export default OffersTab;
